import { h, Fragment, } from "@stencil/core";
import { ICON_NAMESPACE } from "../../../icons/namespace";
/** Create a nested table from data. */
export class TableNested {
  constructor() {
    this.renderExpansion = (depth) => (h, key) => {
      const defaultExpanded = this.defaultExpanded?.(key, depth) ?? 0;
      const expanded = this.expanded.has(key) || defaultExpanded > 0;
      const nestedActive = !expanded && this.activePath && this.activePath[depth] === key
        ? this.activePath[depth + 1]
        : false;
      if (!expanded && !nestedActive)
        return null;
      const count = this.expanded.get(key) ?? defaultExpanded ?? this.expandBy;
      const canExpandMore = !nestedActive && this.canExpandMore(key, count);
      return (h(Fragment, null, h("es-table", { headless: true, class: { nested: true, can_expand_more: canExpandMore }, rowTakesFocus: this.nestedRowTakesFocus ?? this.rowTakesFocus, identifier: this.nestedIdentifier, getCellData: this.getNestedCellData ?? this.getCellData, cells: this.cellsWithExpander(), columns: this.nestedColumns ?? this.columns, rows: nestedActive
          ? [nestedActive]
          : this.getNestedRows?.(key, count) ?? [], renderExpansion: this.renderExpansion(depth + 1), rowClass: this.rowClassWithDefaults(depth + 1), getRowKey: this.getNestedRowKey ?? this.getRowKey }), canExpandMore && (h("es-button", { class: 'expand_more', variant: 'minimal', onClick: this.expandMore(key, count) }, `Show next ${this.expandBy} rows`))));
    };
    this.nestedExtraCellProps = (depth) => (key, data) => ({
      canExpand: this.canExpand(key, data, depth),
      canExpandMore: this.canExpandMore(key, data),
      expanded: this.expanded.has(key),
      loading: this.loadingExpansions.has(key),
      toggleExpansion: () => this.toggleExpansion(key, data),
      ...(this.extraCellProps?.(key, data) ?? {}),
    });
    this.toggleExpansion = async (key, data) => {
      if (this.loadingExpansions.has(key))
        return;
      if (this.expanded.has(key)) {
        this.expanded.delete(key);
        this.expanded = new Map(this.expanded);
        return;
      }
      if (!this.loadNested) {
        this.expanded.set(key, this.expandBy);
        this.expanded = new Map(this.expanded);
        return;
      }
      this.loadingExpansions.add(key);
      this.loadingExpansions = new Set(this.loadingExpansions);
      await this.loadNested(key, data);
      this.loadingExpansions.delete(key);
      this.loadingExpansions = new Set(this.loadingExpansions);
      this.expanded.set(key, this.expandBy);
      this.expanded = new Map(this.expanded);
    };
    this.rowClassWithDefaults = (depth) => (data, key) => {
      if (!this.canExpand(key, data, depth)) {
        return this.rowClass?.(data, key);
      }
      const classes = this.rowClass?.(data, key);
      const extraClasses = !classes
        ? {}
        : typeof classes === 'string'
          ? { [classes]: true }
          : classes;
      return {
        can_expand: true,
        can_expand_more: this.canExpandMore(key, this.expanded.get(key) ?? this.expandBy),
        expanded: this.expanded.has(key),
        click_to_toggle: this.toggleRowOnClick,
        ...extraClasses,
      };
    };
    this.toggleIfRequested = (e) => {
      if (!this.toggleRowOnClick)
        return;
      return this.toggleExpansion(e.detail.key, e.detail.data);
    };
    this.expandMore = (key, currentCount) => async (e) => {
      e.stopPropagation();
      this.expanded.set(key, currentCount + this.expandBy);
      this.expanded = new Map(this.expanded);
    };
    this.cellsWithExpander = () => ({
      '--': {
        title: '',
        variant: 'borderless',
      },
      '--borderless': {
        title: '',
        variant: 'borderless',
      },
      '--no-pad': {
        title: '',
        variant: 'no-pad',
      },
      '--expander': {
        title: '',
        width: '50px',
        variant: 'no-pad',
        cell: (h, { canExpand, loading, expanded, toggleExpansion }) => {
          if (!canExpand)
            return null;
          return (h("es-button", { class: 'expander', variant: 'minimal', onClick: toggleExpansion }, h("es-icon", { size: 18, icon: loading
              ? [ICON_NAMESPACE, 'spinner']
              : [ICON_NAMESPACE, 'chevron'], angle: expanded ? 180 : 0 })));
        },
      },
      ...this.cells,
    });
    this.headless = false;
    this.stickyHeader = false;
    this.outerIdentifier = 'table';
    this.getCellData = undefined;
    this.getRowKey = undefined;
    this.getNestedRowKey = undefined;
    this.cells = undefined;
    this.columns = Object.keys(this.cells);
    this.rows = undefined;
    this.linkRowTo = undefined;
    this.rowTakesFocus = undefined;
    this.rowClass = () => undefined;
    this.toggleRowOnClick = false;
    this.extraCellProps = undefined;
    this.nestedIdentifier = 'nested-table';
    this.nestedColumns = undefined;
    this.getNestedRows = undefined;
    this.getNestedCellData = undefined;
    this.loadNested = undefined;
    this.nestedRowTakesFocus = undefined;
    this.canExpand = () => true;
    this.canExpandMore = () => false;
    this.expandBy = 10;
    this.defaultExpanded = undefined;
    this.activePath = undefined;
    this.loading = undefined;
    this.loadingRows = 1;
    this.expanded = new Map();
    this.loadingExpansions = new Set();
  }
  render() {
    return (h("es-table", { stickyHeader: this.stickyHeader, headless: this.headless, rowTakesFocus: this.rowTakesFocus, identifier: this.outerIdentifier, getCellData: this.getCellData, cells: this.cellsWithExpander(), columns: this.columns, rows: this.rows, renderExpansion: this.renderExpansion(0), rowClass: this.rowClassWithDefaults(0), onClickRow: this.toggleIfRequested, extraCellProps: this.nestedExtraCellProps(0), getRowKey: this.getRowKey, loading: this.loading, loadingRows: this.loadingRows }));
  }
  static get is() { return "es-table-nested"; }
  static get originalStyleUrls() {
    return {
      "$": ["es-table-nested.css"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["es-table-nested.css"]
    };
  }
  static get properties() {
    return {
      "headless": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Do not render header."
        },
        "attribute": "headless",
        "reflect": false,
        "defaultValue": "false"
      },
      "stickyHeader": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Header sticks to scroll parent."
        },
        "attribute": "sticky-header",
        "reflect": false,
        "defaultValue": "false"
      },
      "outerIdentifier": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Passed to cell renderer as `parent`."
        },
        "attribute": "outer-identifier",
        "reflect": false,
        "defaultValue": "'table'"
      },
      "getCellData": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(key: string) => any",
          "resolved": "((key: string) => any) | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Sync function for extracting the data from the row. By default, it assumes you passed an array of data as your columns."
        }
      },
      "getRowKey": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(row: any, i: number) => string",
          "resolved": "((row: any, i: number) => string) | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Sync function for extracting a key from your row data. By default, if the passed rows are strings it will use them directly, otherwise it will warn and use the index."
        }
      },
      "getNestedRowKey": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(row: any, i: number) => string",
          "resolved": "((row: any, i: number) => string) | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Sync function for extracting a key from your nested row data. By default, if the passed rows are strings it will use them directly, otherwise it will warn and use the index."
        }
      },
      "cells": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TableCells<any, any>",
          "resolved": "{ [x: string]: TableCell<any, any>; }",
          "references": {
            "TableCells": {
              "location": "import",
              "path": "../types",
              "id": "src/components/tables/types.ts::TableCells"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "A record of table cell definitions.Some built in cells are cells are available for use:\n- `--borderless`: A blank placeholder cell with no border, for aligning with the parent cell.\n- `--no-pad`: A blank placeholder cell, for aligning with the parent cell.\n- `--expander`: The expander button."
        }
      },
      "columns": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "string[]",
          "resolved": "string[] | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "The order and keys of the cells to be rendered. If omitted, all cells will be rendered."
        },
        "defaultValue": "Object.keys(this.cells)"
      },
      "rows": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "any[]",
          "resolved": "any[]",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "An array of rows to render. Each item in the array is passed to getCellData, to allow passing keys or other identifiers."
        }
      },
      "linkRowTo": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(row: any) => string",
          "resolved": "((row: any) => string) | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "A function to calculate a href from the cell data."
        }
      },
      "rowTakesFocus": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "If rows should be allowed to take focus"
        },
        "attribute": "row-takes-focus",
        "reflect": false
      },
      "rowClass": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(\n        row: any,\n        key: string,\n    ) => Record<string, boolean> | string | undefined",
          "resolved": "(row: any, key: string) => string | Record<string, boolean> | undefined",
          "references": {
            "Record": {
              "location": "global",
              "id": "global::Record"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "A function to calculate the class or classes of the row from the cellData."
        },
        "defaultValue": "() => undefined"
      },
      "toggleRowOnClick": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "If clicking a row should expand it."
        },
        "attribute": "toggle-row-on-click",
        "reflect": false,
        "defaultValue": "false"
      },
      "extraCellProps": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(key: string, data: any) => Record<string, any>",
          "resolved": "((key: string, data: any) => Record<string, any>) | undefined",
          "references": {
            "Record": {
              "location": "global",
              "id": "global::Record"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Pass extra props to cells"
        }
      },
      "nestedIdentifier": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Passed to cell renderer as `parent`."
        },
        "attribute": "nested-identifier",
        "reflect": false,
        "defaultValue": "'nested-table'"
      },
      "nestedColumns": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "string[]",
          "resolved": "string[] | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "The order and keys of the cells to be rendered in a nested table. If omitted, all cells will be rendered."
        }
      },
      "getNestedRows": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(key: string, count: number) => any[] | undefined",
          "resolved": "((key: string, count: number) => any[] | undefined) | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Sync function for extracting a list of rows for the nested table"
        }
      },
      "getNestedCellData": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(key: string) => any",
          "resolved": "((key: string) => any) | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Sync function for extracting the data from the nested row. By default, it assumes you passed an array of data as your columns."
        }
      },
      "loadNested": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(key: string, data: any) => Promise<void>",
          "resolved": "((key: string, data: any) => Promise<void>) | undefined",
          "references": {
            "Promise": {
              "location": "global",
              "id": "global::Promise"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "async function for loading nested data when a row is expanded."
        }
      },
      "nestedRowTakesFocus": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "If the nested rows should be allowed to take focus."
        },
        "attribute": "nested-row-takes-focus",
        "reflect": false
      },
      "canExpand": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(key: string, data: any, depth: number) => boolean",
          "resolved": "(key: string, data: any, depth: number) => boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Function to decide if a row can take expand, to show a nested table."
        },
        "defaultValue": "() => true"
      },
      "canExpandMore": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(key: string, count: number) => boolean",
          "resolved": "(key: string, count: number) => boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Function to decide if a row can expand more, to show more rows in the nested table."
        },
        "defaultValue": "() =>\n        false"
      },
      "expandBy": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Number number of rows to display in expansion"
        },
        "attribute": "expand-by",
        "reflect": false,
        "defaultValue": "10"
      },
      "defaultExpanded": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "(key: string, depth: number) => number",
          "resolved": "((key: string, depth: number) => number) | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Number of rows to be expanded by default"
        }
      },
      "activePath": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "string[]",
          "resolved": "string[] | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "A path to a the currently active row, to auto expand its parent and show it as selected."
        }
      },
      "loading": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Indicates if the loading indicators should be displayed"
        },
        "attribute": "loading",
        "reflect": false
      },
      "loadingRows": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Specifies the number of rows to display when loading is true. Defaults to 1."
        },
        "attribute": "loading-rows",
        "reflect": false,
        "defaultValue": "1"
      }
    };
  }
  static get states() {
    return {
      "expanded": {},
      "loadingExpansions": {}
    };
  }
  static get events() {
    return [{
        "method": "clickRow",
        "name": "clickRow",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": "Triggered whenever a row (or nested row) is clicked. The `detail` is the item in the row array."
        },
        "complexType": {
          "original": "any",
          "resolved": "any",
          "references": {}
        }
      }, {
        "method": "expansion",
        "name": "expansion",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": "Triggered whenever a row is expanded."
        },
        "complexType": {
          "original": "{ data: any; key: string }",
          "resolved": "{ data: any; key: string; }",
          "references": {}
        }
      }];
  }
}
//# sourceMappingURL=es-table-nested.js.map
